import * as React from 'react';
import Swipe from 'react-easy-swipe';
import Tlass from './cssClasses';
import { cssTranslate, isKeyboardEvent, outerWidth} from './utils';
import { IThumbsProps, IThumbsState} from './ICarousel';
import { defaultThumbsProps} from './defaultProps';
import classnames from 'classnames';

interface IAnyAction {
    type: string;
    [key: string]: any;
}

interface IItemListStyles {
    transform?: string;
    transitionDuration?: string;
}

export const Thumbs =React.forwardRef((props: IThumbsProps, ref: any): React.ReactElement => {

    const itemsWrapperRef = React.useRef<any>();
    const itemsListRef = React.useRef<HTMLUListElement>();
    const thumbsRef = React.useRef<HTMLLIElement[]>();
    const firstUpdate = React.useRef({initial: false, children: false});

    const initalState = {
        selectedItem: props.selectedItem,
        swiping: false,
        showArrows: false,
        firstItem: 0,
        itemSize: 0,
        visibleItems: 0,
        lastPosition: 0,
    };
    const currentItem = props.selectedItem;

    const reducer: React.Reducer<IThumbsState, IAnyAction> = (state, action) =>{
        const type = action.type;

        switch(type) {
            case 'selectedItem':
            case 'swiping':
            case 'itemSize':
            case 'showArrows':
            case 'firstItem':
            case 'visibleItems':
            case 'lastPosition':
                return {
                    ...state,
                    ...action[type]
                };
            case 'all':
                return {
                    ...state,
                    ...action.all
                };
            default:
                return state;
        }
    };

    const [state, changeState] = React.useReducer(reducer, {
        ...initalState
    });

    const isSwipeable = false;

    // const hasPrev = state.showArrows && state.firstItem > 0;

    // const hasNext = state.showArrows && state.firstItem < state.lastPosition;

    React.useImperativeHandle(ref, ()=>({

        updateSizes: ()=>{
            updateSizes();
        }
    }));

    function setupThumbs(): void {
        // getWindow().addEventListener('resize', updateSizes);
        // getWindow().addEventListener('DOMContentLoaded', updateSizes);

        updateSizes();

    }

    function destroyThumbs(): void {
        // getWindow().removeEventListener('resize', updateSizes);
        // getWindow().removeEventListener('DOMContentLoaded', updateSizes);
    }

    const getFirstItem = React.useCallback((selectedItem: number)=>{
        let firstItem = selectedItem;

        if(selectedItem >= state.lastPosition) {
            firstItem = state.lastPosition;
        }

        if(selectedItem < state.firstItem + state.visibleItems) {
            firstItem = state.firstItem;
        }

        if(firstItem < state.firstItem) {
            firstItem = selectedItem;
        }

        return firstItem;
    }, [state.firstItem, state.lastPosition, state.visibleItems]);

    const updateSizes = React.useCallback(()=>{
        if(!props.children || !itemsWrapperRef.current || !thumbsRef.current) {
            return;
        }
        const total = React.Children.count(props.children);
        const wrapperSize = itemsWrapperRef.current.clientWidth;
        const itemSize = props.thumbWidth && typeof props.thumbWidth === 'number' ? props.thumbWidth: outerWidth(thumbsRef.current[0]);
        const visibleItems = Math.floor(wrapperSize / itemSize);
        const lastPosition = total - visibleItems;
        const showArrows =visibleItems < total;
        changeState({type: 'all', all: {itemSize: itemSize, visibleItems: visibleItems, firstItem: showArrows ? getFirstItem(props.selectedItem) : 0, lastPosition: lastPosition, showArrows: showArrows }});
    }, [getFirstItem, props.children, props.selectedItem, props.thumbWidth]);

    const setThumbRef = (node: HTMLLIElement, index: number): void => {
        if(!thumbsRef.current) {
            thumbsRef.current = [];
        }
        thumbsRef.current[index] = node;
    };

    const setItemsListRef = (node: HTMLUListElement): void => {
        itemsListRef.current = node;
    };

    const handleClickItem = (index: number, item: React.ReactNode, e: React.MouseEvent | React.KeyboardEvent): void => {
        if(!isKeyboardEvent(e) || e.key === 'Enter') {
            const handler = props.onSelectItem;

            if(typeof handler === 'function') {
                handler(index, item);
            }
        }
    };

    const onSwipeStart = (): void => {
        changeState({type: 'swiping', swiping: {swiping: true}});
    };

    const onSwipeEnd = (): void => {
        changeState({type: 'swiping', swiping: {swiping: false}});
    };

    const onSwipeMove = (delta: { x: number; y: number;}): boolean => {
        let deltaX = delta.x;
        if(!state.itemSize || !itemsWrapperRef.current) {
            return false;
        }
        const leftBoundary = 0;

        const currentPosition = - state.firstItem * state.itemSize;

        const lastLeftBoundary = - state.visibleItems * state.itemSize;

        if(currentPosition === leftBoundary && deltaX > 0) {
            deltaX =0;
        }

        if(currentPosition === lastLeftBoundary && deltaX <0) {
            deltaX =0;
        }

        const wrapperSize = itemsWrapperRef.current.clientWidth;
        const position = currentPosition + 100 / (wrapperSize / deltaX);
        if(itemsListRef.current) {
            itemsListRef.current.style['transform'] = cssTranslate(position, '%', props.axis);
        }
        return true;
    };

    const slideRight = (position?: number): void => {
        moveThumbs(state.firstItem - (typeof position === 'number' ? position: 1));
    };

    const slideLeft = (position?: number): void => {
        moveThumbs(state.firstItem + (typeof position === 'number' ? position: 1));
    };

    const moveThumbs = (position: number): void => {
        position = position <0 ? 0: position;
        position = position >= state.lastPosition ? state.lastPosition : position;
        changeState({type: 'firstItem', firstItem: {firstItem: position}});
    };

    React.useEffect(() => {
        setupThumbs();

        return () => {
            destroyThumbs();
        };
    }, []);

    React.useEffect(() => {
        if(props.selectedItem !== state.selectedItem) {
            changeState({type: 'all', all: {selectedItem: props.selectedItem, firstItem: getFirstItem(props.selectedItem)}});
        }
        updateSizes();

    }, [getFirstItem, props, state.selectedItem, updateSizes]);

    React.useEffect(() => {
        if(firstUpdate.current.children) {
            updateSizes();
        }

    }, [props.children, updateSizes]);

    const getItemStyles = (state: any): IItemListStyles => {
        const itemListStyle: IItemListStyles = {};

        const currentPosition = - state.firstItem * (state.itemSize ?? 0);

        const transformProp = cssTranslate(currentPosition, 'px', props.axis);

        const transitionTime = props.transitionTime + 'ms';

        itemListStyle.transform = transformProp;

        itemListStyle.transitionDuration = transitionTime;

        return itemListStyle;
    };

    function renderItems(): JSX.Element[] {
        return props.children!.map((img, index) =>{
            const itemClass = Tlass.item(false, index === currentItem);
            const otherClass = props.thumbClassList?.thumbItemClassName === undefined ? '': props.thumbClassList?.thumbItemClassName;

            const thumbProps = {
                key: index,
                ref: (e: HTMLLIElement) => setThumbRef(e, index),
                className: classnames(`${itemClass}`, `${otherClass}`),
                onClick: (e: React.MouseEvent | React.KeyboardEvent) => { handleClickItem(index, props.children![index], e); },
                onKeyDown: (e: React.MouseEvent | React.KeyboardEvent) => { handleClickItem( index, props.children![index], e); },
                'aria-label': `${props.labels.item} ${index + 1}`,
            };
            return (
                <li {...thumbProps} role='button' tabIndex={0} >
                    {img}
                </li>
            );
        });

    }

    return (
        <div className={classnames(`${Tlass.carousel(false)}`, `${props.thumbClassList?.thumbClassName === undefined?'': props.thumbClassList?.thumbClassName}`)} ref={ref}>
            <div className= {Tlass.wrapper(false)} ref={itemsWrapperRef}>
                {/* <button
                    type='button'
                    className={Tlass.ARROW_PREV(!hasPrev)}
                    onClick={() => { slideRight(); }}
                    aria-label={props.labels.leftArrow}
                /> */}
                {isSwipeable ?(
                    <Swipe
                        tagName= 'ul'
                        className={Tlass.slider(false, state.swiping)}
                        onSwipeLeft={slideLeft}
                        onSwipeRight={slideRight}
                        onSwipeMove={onSwipeMove}
                        onSwipeStart={onSwipeStart}
                        onSwipeEnd={onSwipeEnd}
                        style={getItemStyles(state)}
                        innerRef={setItemsListRef}
                    >
                        {renderItems()}
                    </Swipe>
                ):(
                    <ul
                        className={Tlass.slider(false, state.swiping)}
                        ref={(node: HTMLUListElement) => setItemsListRef(node)}
                        style={getItemStyles(state)}
                    >
                        {renderItems()}
                    </ul>
                )}
                {/* <button
                    type='button'
                    className={Tlass.ARROW_NEXT(!hasNext)}
                    onClick={()=> { slideLeft(); }}
                    aria-label={props.labels.rightArrow}
                /> */}
            </div>

        </div>
    );

});

Thumbs.defaultProps = defaultThumbsProps;
